// ./src/utils/api/PrintEngineService.ts
//
// THE PRINT ENGINE's two catalogs, in one file because they are only ever used together: you cannot
// print without both a DESIGN (what a card looks like) and a MEDIA (what the paper is).
//
// ⚠ THE SPLIT IS THE POINT. The same ticket design prints 1-up on Letter, 10-up on an Avery 16154 sheet,
// and one-at-a-time on a DYMO roll. Keeping appearance and geometry apart is what stops the catalog
// becoming designs × stocks. Mirrors the eventsmagic-api PrintDesign / PrintMedia SDL.
//
// ⚠ EVERY LENGTH IS MILLIMETRES AND ARRIVES AS A NUMBER. The server converts its `decimal` columns on
// the way out precisely so this side can multiply them; if one ever comes back a string, the page layout
// silently produces a sheet the width of a wall (`2 * "139.7"` is 279.4, but `"139.7" + "139.7"` is
// "139.7139.7"). Treat a string here as a server bug, not something to coerce away quietly.
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';
import { listAllPages, ListAllOptions } from './listAllPages';
import { Pagination, SortItem } from './commonApiTypes';

// ⚠⚠ `PaginationAndSorting` IS NESTED: `{ pagination: { page, pageSize }, sortBy: [...] }`. Spreading
// `page`/`pageSize` straight into `params` is rejected by the GATEWAY before the query ever reaches the
// subgraph — "Field \"page\" is not defined by type \"PaginationAndSorting\"" — and `tsc` cannot see it,
// because the variables object handed to `executeQuery` is untyped and the query is a gql template
// literal. Every other list service in this folder nests it; so does this one now.

// =============================================================================
// Constants — mirror the server boundary (src/boundary/print.ts)
// =============================================================================

export const PRINT_MEDIA_KIND = {
  SHEET: 'sheet',
  ROLL: 'roll',
} as const;

export const PRINT_DESIGN_KIND = {
  TICKET: 'ticket',
  NAME_BADGE: 'name_badge',
  /**
   * ⚠ A PLACE CARD IS NOT A NAME BADGE (owner, 2026-09-23). They share a renderer and most of their
   * fields — which is why they started out as one kind — but to a HOST they are different objects: a
   * badge is pinned to a person, a place card stands on a table saying where to sit. They are reached
   * from different places, print on different stock, and more seating stationery is planned.
   * ⚠ Mirrors `boundary/print.ts`; changing one without the other makes the server refuse the pairing.
   */
  PLACE_CARD: 'place_card',
  /**
   * ⚠ A COUPON IS A TICKET'S SHAPE, NOT A TICKET (ROADMAP A5 item 4). It resolves different tokens —
   * its headline is what it GIVES, which no ticket has — so a ticket design pointed at one would print
   * a blank card. ⚠ Mirrors `boundary/print.ts`; changing one without the other makes the server
   * refuse the pairing.
   */
  COUPON: 'coupon',
  /** ROADMAP #172 — a QR tag: drawn by the coupon's renderer with the tag's rows. Mirrors `boundary/print.ts`. */
  QR_TAG: 'qr_tag',
} as const;

export const PRINT_RENDER_MODE = {
  BUILTIN: 'builtin',
  HTML: 'html',
} as const;

export const PRINT_BUILTIN_LAYOUT = {
  TICKET_STUB: 'ticket_stub',
  TICKET_PLAIN: 'ticket_plain',
  BADGE_CLASSIC: 'badge_classic',
  /** A place card on stock that FOLDS: the top half prints inverted so the tent reads from both sides. */
  PLACE_CARD_TENT: 'place_card_tent',
  /** A coupon voucher: what it gives, its value, and the code the vendor scans. */
  COUPON_VOUCHER: 'coupon_voucher',
  /** #172 — a QR tag: what it is, its number, its session and its code. */
  QR_TAG_CARD: 'qr_tag_card',
} as const;

export const PRINT_MEDIA_VENDOR_LABELS: Record<string, string> = {
  avery: 'Avery',
  dymo: 'DYMO',
  zebra: 'Zebra',
  generic: 'Plain paper',
};

export const printMediaVendorLabel = (vendor?: string | null): string =>
  (vendor && PRINT_MEDIA_VENDOR_LABELS[vendor]) || vendor || '—';

/** The SYSTEM account id — a row owned by it is a seeded built-in and is read-only. */
export const SYSTEM_ACCOUNT_ID = '00000000-0000-0000-0000-000000000000';

export const isBuiltIn = (row?: { accountId?: string | null } | null): boolean =>
  row?.accountId === SYSTEM_ACCOUNT_ID;

// =============================================================================
// Types
// =============================================================================

export interface PrintMedia {
  id?: string;
  accountId?: string | null;
  name?: string | null;
  description?: string | null;
  vendor?: string | null;
  vendorCode?: string | null;
  mediaKind?: string | null;
  orientation?: string | null;
  pageWidthMm?: number | null;
  pageHeightMm?: number | null;
  marginTopMm?: number | null;
  marginLeftMm?: number | null;
  columnCount?: number | null;
  rowCount?: number | null;
  cellWidthMm?: number | null;
  cellHeightMm?: number | null;
  gutterXMm?: number | null;
  gutterYMm?: number | null;
  cornerRadiusMm?: number | null;
  settings?: Record<string, any> | null;
  orderNo?: number | null;
  status?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface PrintDesign {
  id?: string;
  accountId?: string | null;
  name?: string | null;
  description?: string | null;
  kind?: string | null;
  renderMode?: string | null;
  builtinLayout?: string | null;
  bodyHtml?: string | null;
  bodyCss?: string | null;
  backgroundUploadedFileId?: string | null;
  /**
   * A stock pick (Unsplash / GIPHY / Pexels) or a pasted link — what the shared media picker returns
   * INSTEAD of a file id. ⚠ The uploaded file wins when a design somehow carries both, and it is the
   * durable half: a hotlinked image can 404 later, which on paper is blank stock rather than a broken
   * image on a page.
   */
  backgroundUrl?: string | null;
  preferredMediaId?: string | null;
  settings?: Record<string, any> | null;
  orderNo?: number | null;
  status?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** What a design write accepts — mirrors `PrintDesignInput` in the SDL. */
export interface PrintDesignInput {
  name?: string | null;
  description?: string | null;
  kind?: string | null;
  renderMode?: string | null;
  builtinLayout?: string | null;
  bodyHtml?: string | null;
  bodyCss?: string | null;
  backgroundUploadedFileId?: string | null;
  backgroundUrl?: string | null;
  preferredMediaId?: string | null;
  orderNo?: number | null;
  /** ⚠ Replaces the blob wholesale on update — send the whole object, never a patch. */
  settings?: Record<string, any> | null;
}

export interface PrintMediaFilter {
  id?: string[];
  vendor?: string[];
  mediaKind?: string[];
  searchKeyword?: string;
}

export interface PrintDesignFilter {
  id?: string[];
  kind?: string[];
  renderMode?: string[];
  searchKeyword?: string;
}

// ⚠ `cellsPerPage` and the cell arithmetic deliberately DO NOT live here. They are in
// `components/print/cardGeometry.ts`, which has no imports so a guard can execute it — and one
// definition means the renderer and every caller cannot drift apart on how big a page is.

// =============================================================================
// GraphQL
// =============================================================================

const MEDIA_FIELDS = `
  id
  accountId
  name
  description
  vendor
  vendorCode
  mediaKind
  orientation
  pageWidthMm
  pageHeightMm
  marginTopMm
  marginLeftMm
  columnCount
  rowCount
  cellWidthMm
  cellHeightMm
  gutterXMm
  gutterYMm
  cornerRadiusMm
  settings
  orderNo
  status
`;

const DESIGN_FIELDS = `
  id
  accountId
  name
  description
  kind
  renderMode
  builtinLayout
  bodyHtml
  bodyCss
  backgroundUploadedFileId
  backgroundUrl
  preferredMediaId
  settings
  orderNo
  status
`;

const MEDIA_LIST_QUERY = gql`
  query PrintMediaList($filter: PrintMediaFilter, $params: PaginationAndSorting) {
    printMediaList(filter: $filter, params: $params) {
      code
      data { ${MEDIA_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const DESIGN_LIST_QUERY = gql`
  query PrintDesignList($filter: PrintDesignFilter, $params: PaginationAndSorting) {
    printDesignList(filter: $filter, params: $params) {
      code
      data { ${DESIGN_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Services
// =============================================================================

export class PrintMediaService {
  static async list(filter?: PrintMediaFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(MEDIA_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * ⚠ Pages through the WHOLE catalog. A print page that fetched one page of stock would silently offer
   * a subset of the paper a host owns, and a picker missing an entry looks exactly like a picker that
   * simply does not support that paper. See `listAllPages`.
   */
  static async listAll(filter?: PrintMediaFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<PrintMedia>(PrintMediaService.list, filter, sortBy, opts);
  }
}

const DESIGN_CREATE_MUTATION = gql`
  mutation PrintDesignCreate($params: PrintDesignInput) {
    printDesignCreate(params: $params) {
      code
      data { ${DESIGN_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const DESIGN_UPDATE_MUTATION = gql`
  mutation PrintDesignUpdate($where: PrintDesignWhereInput, $params: PrintDesignInput) {
    printDesignUpdate(where: $where, params: $params) {
      code
      data { ${DESIGN_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

export class PrintDesignService {
  static async list(filter?: PrintDesignFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(DESIGN_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async listAll(filter?: PrintDesignFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<PrintDesign>(PrintDesignService.list, filter, sortBy, opts);
  }

  /**
   * ⚠ WRITES ARE NEW IN PHASE 3. Until a host could bring their own artwork, every design was a SEEDED
   * built-in owned by SYSTEM and this service was read-only. The server already refuses a write to one
   * of those (`loadOwned` + `isOwn`), so a host's design is theirs and the built-ins stay everyone's.
   */
  static async create(params: PrintDesignInput): Promise<OpResult> {
    return ApiService.executeMutation(DESIGN_CREATE_MUTATION, { params });
  }

  static async update(id: string, params: PrintDesignInput): Promise<OpResult> {
    return ApiService.executeMutation(DESIGN_UPDATE_MUTATION, { where: { id }, params });
  }
}
